import { UserRole } from '@nextforce/api'
import { Button, makeStyles, Text } from '@rneui/themed'
import { t } from 'app/i18l'
import React, { useState } from 'react'
import {
  Platform,
  StyleProp,
  TextStyle,
  useWindowDimensions,
  View,
  ViewStyle,
} from 'react-native'
import {
  Menu,
  MenuOptions,
  MenuTrigger,
  renderers,
} from 'react-native-popup-menu'
import { Cell, Table, TableWrapper } from 'react-native-table-component'
import Spinner from 'shared/components/spinner'
import { WEB_SCREEN_WIDTH } from 'shared/constants'
import { useWeeklySummary } from './queries/list'
import { WeeklySummaryData, WeeklySummaryItem } from './queries/shared/types'

export interface MenuProps {
  role: UserRole
  storeId?: string
  isHelperWeeklySummary?: boolean
  menuRef: React.MutableRefObject<Menu | null | undefined>
  setShowWeeklySummary: React.Dispatch<React.SetStateAction<boolean>>
}

export const MENU_TRIGGER_HEIGHT = 60

export function WeeklySummaryMenu({
  role,
  storeId,
  isHelperWeeklySummary,
  menuRef,
  setShowWeeklySummary,
}: MenuProps) {
  const styles = useStyles()

  const weeklySummaryQuery = useWeeklySummary(isHelperWeeklySummary, storeId)

  const closeText = t('closeWeeklySummary')
  const openText = t('seeWeeklySummary')

  const [triggerText, setTriggerText] = useState<string>(openText)

  if (role === 'helper' && !isHelperWeeklySummary) {
    return null
  }

  return (
    <Menu
      renderer={renderers.SlideInMenu}
      ref={ref => {
        menuRef.current = ref
      }}
      onOpen={() => setTriggerText(closeText)}
      onClose={() => {
        setTriggerText(openText)
        setShowWeeklySummary(false)
      }}
    >
      <MenuTrigger>
        {Platform.OS !== 'web' && (
          <Text style={styles.menuTriggerText}>{triggerText}</Text>
        )}
      </MenuTrigger>
      <MenuOptions>
        <MenuStatusBoundary
          isLoading={weeklySummaryQuery.isLoading}
          error={weeklySummaryQuery.error}
        >
          <View style={styles.menuContainer}>
            <WeeklySummaryTable
              summary={weeklySummaryQuery.data}
              isHelperWeeklySummary={isHelperWeeklySummary}
            />
            <Button
              title={t('gotIt')}
              containerStyle={styles.buttonContainer}
              onPress={async () => {
                await menuRef.current?.close()
              }}
            />
          </View>
        </MenuStatusBoundary>
      </MenuOptions>
    </Menu>
  )
}

interface MenuStatusBoundaryProps {
  isLoading?: boolean
  error?: any
  children: React.ReactNode
}

function MenuStatusBoundary({
  isLoading,
  error,
  children,
}: MenuStatusBoundaryProps) {
  const styles = useStyles()

  if (isLoading || error) {
    return (
      <View style={styles.menuStatus}>
        {isLoading ? (
          <Spinner size="large" />
        ) : (
          <Text style={styles.errorText}>{t('notFound')}</Text>
        )}
      </View>
    )
  }

  return <>{children}</>
}

const getNamesColumnWidthPercentage = (isHelperWeeklySummary?: boolean) =>
  isHelperWeeklySummary ? 0.24 : 0.29
const WEEK_COLUMNS_WIDTH_PERCENTAGE = 0.19

interface WeeklySummaryTableProps {
  summary?: WeeklySummaryData
  isHelperWeeklySummary?: boolean
}

function WeeklySummaryTable({
  summary,
  isHelperWeeklySummary,
}: WeeklySummaryTableProps) {
  const styles = useStyles({ isHelperWeeklySummary })

  const { width } = useWindowDimensions()

  if (!summary) {
    return null
  }

  const namesColumnWidthPercentage = getNamesColumnWidthPercentage(
    isHelperWeeklySummary
  )

  const visibleWidth = Platform.OS === 'web' ? WEB_SCREEN_WIDTH : width

  return (
    <View style={styles.container}>
      <Table style={styles.table}>
        <Column
          dataArray={summary.names}
          style={styles.namesColumn}
          width={namesColumnWidthPercentage * visibleWidth}
          textStyle={styles.namesText}
        />
        <Column
          dataArray={summary.lastWeek}
          style={styles.lastWeekColumn}
          hasColumnHeader
          width={WEEK_COLUMNS_WIDTH_PERCENTAGE * visibleWidth}
          textStyle={styles.lastWeekText}
        />
        <Column
          dataArray={summary.thisWeek}
          style={styles.thisWeekColumn}
          hasColumnHeader
          width={WEEK_COLUMNS_WIDTH_PERCENTAGE * visibleWidth}
          textStyle={styles.thisWeekText}
        />
        <Column
          dataArray={summary.trend}
          style={styles.trendColumn}
          hasColumnHeader
          width={WEEK_COLUMNS_WIDTH_PERCENTAGE * visibleWidth}
          textStyle={styles.trendText}
        />
      </Table>
    </View>
  )
}

interface ColumnProps {
  dataArray: WeeklySummaryItem[]
  style?: ViewStyle
  hasColumnHeader?: boolean
  width?: number
  textStyle?: StyleProp<TextStyle>
}

const CELL_HEIGHT = 40

function Column({
  dataArray,
  style,
  hasColumnHeader,
  width,
  textStyle,
}: ColumnProps) {
  const styles = useStyles()

  const tableWrapperStyle: StyleProp<ViewStyle> = style ? style : {}

  const cellTextStyle = (
    cellData: WeeklySummaryItem,
    index: number
  ): StyleProp<TextStyle> =>
    Object.assign(
      {},
      textStyle ?? {},
      hasColumnHeader && index === 0
        ? styles.columnHeaderText
        : cellData.isBad
        ? styles.badCellText
        : {},
      styles.cellText
    )
  return (
    <TableWrapper style={tableWrapperStyle}>
      {dataArray.map((cellData: WeeklySummaryItem, index: number) => (
        <Cell
          data={
            typeof cellData.body === 'string'
              ? t(cellData.body)
              : `${cellData.isAmount ? '$' : ''}${cellData.body}`
          }
          style={styles.cell}
          textStyle={cellTextStyle(cellData, index)}
          width={width}
          height={CELL_HEIGHT}
          key={String(index)}
        />
      ))}
    </TableWrapper>
  )
}

interface StyleProps {
  isHelperWeeklySummary?: boolean
}

const useStyles = makeStyles((theme, props?: StyleProps) => ({
  container: {
    flex: 1,
    paddingTop: 20,
    marginVertical: 10,
    marginHorizontal: 12,
    backgroundColor: theme.colors?.white,
    justifyContent: 'center',
    alignItems: 'center',
  },
  menuContainer: {
    backgroundColor: theme.colors?.white,
    borderTopWidth: 2,
    borderTopColor: theme.colors?.grey2,
  },
  menuTriggerText: {
    textAlign: 'center',
    textAlignVertical: 'center',
    backgroundColor: theme.colors?.grey5,
    color: theme.colors?.blue,
    height: MENU_TRIGGER_HEIGHT,
    ...Platform.select({
      ios: {
        lineHeight: MENU_TRIGGER_HEIGHT,
      },
      android: {},
      default: {},
    }),
  },
  buttonContainer: {
    margin: 10,
  },
  table: {
    flexDirection: 'row',
    marginHorizontal: props?.isHelperWeeklySummary ? 10 : 1,
  },
  namesColumn: {
    marginTop: CELL_HEIGHT,
    borderColor: theme.colors?.black,
    marginBottom: props?.isHelperWeeklySummary ? CELL_HEIGHT : 0,
    borderWidth: 0,
    borderBottomWidth: 1,
  },
  namesText: {
    color: theme.colors?.black,
  },
  lastWeekColumn: {
    backgroundColor: theme.colors?.teal,
    borderColor: theme.colors?.black,
    borderWidth: 0,
    borderBottomWidth: 1,
  },
  lastWeekText: {
    textAlign: 'right',
    color: 'black',
  },
  thisWeekColumn: {
    borderColor: theme.colors?.black,
    borderWidth: 0,
    borderBottomWidth: 1,
  },
  thisWeekText: {
    textAlign: 'right',
    color: theme.colors?.grey1,
    fontWeight: 'bold',
  },
  trendColumn: {
    backgroundColor: theme.colors?.violet,
    borderColor: theme.colors?.black,
    borderWidth: 0,
    borderBottomWidth: 1,
    borderRightWidth: 1,
  },
  trendText: {
    textAlign: 'right',
    color: 'black',
  },
  cell: {
    borderWidth: 0,
    borderTopWidth: 1,
    borderLeftWidth: 1,
  },
  cellText: {
    marginHorizontal: 5,
  },
  columnHeaderText: {
    textAlign: 'center',
    fontWeight: 'bold',
    color: theme.colors?.black,
  },
  badCellText: {
    color: theme.colors.red,
  },
  menuStatus: {
    backgroundColor: theme.colors?.white,
    margin: 10,
  },
  errorText: {
    textAlign: 'center',
    textAlignVertical: 'center',
    fontWeight: 'bold',
    color: theme.colors?.primary,
  },
}))
